import type { PanelEntry } from "../lib/api";
import type { ReactNode } from "react";
import type { ScreenView } from "../lib/screen";
import { Icon } from "../components/Icon";
import { chatHref, WORKSPACE_HREF } from "../connections/navigation";
import { panelHref, workspaceEntries } from "../lib/workspaces";

export interface WorkspaceLink { label: string; href: string; panel_context?: string; context?: Record<string, string>; menu?: false }
export interface WorkspaceNavigation {
  title: string; pages: { panel: string; label: string }[]; links: WorkspaceLink[];
  links_label: string; state_context: Record<string, string>;
}
function object(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}
function fields(value: unknown): Record<string, string> {
  return Object.fromEntries(Object.entries(object(value)).filter((entry): entry is [string, string] => typeof entry[1] === "string" && Boolean(entry[1])));
}

/** An explicit object needs its own identity; automatic page headings name no object. */
export function surfaceObjectTitle(content: unknown, context: Record<string, string>): string | undefined {
  const value = object(content);
  const title = typeof value.title === "string" ? value.title.trim() : "";
  const coordinates = Object.entries(context).filter(([key, coordinate]) => coordinate && key !== "session" && key !== "session_id"
    && key !== "title" && !key.endsWith("_title") && Object.hasOwn(value, key));
  const identities = Object.keys(value).filter(key => (key === "id" || key.endsWith("_id"))
    && !["session_id", "agent_id", "principal_id", "panel_id"].includes(key)
    && typeof value[key] === "string" && Boolean(value[key]));
  return title && (coordinates.length || identities.length) && coordinates.every(([key, coordinate]) => value[key] === coordinate) ? title : undefined;
}

export function surfaceObjectVersion(content: unknown): string | undefined {
  const value = object(content).version ?? object(content).media_version;
  return typeof value === "string" && value.trim() ? value.trim().slice(0, 80)
    : typeof value === "number" && Number.isFinite(value) ? String(value) : undefined;
}

/** Several frames can describe one page; opaque versions belong only in its tooltip. */
export function screenPageLabel(views: ScreenView[]): { title: string; detail: string } | undefined {
  const pages = new Map<string, { title: string; objects: Set<string>; details: Set<string> }>();
  for (const view of views) {
    const page = pages.get(view.panel_key) ?? { title: view.title, objects: new Set<string>(), details: new Set<string>() };
    const title = surfaceObjectTitle(view.content, view.viewContext);
    const version = title ? surfaceObjectVersion(view.content) : undefined;
    if (title) {
      const suffix = version ? ` · 版本 ${version}` : "";
      page.objects.add(`《${title}》${version && /^(?:sha256:)?[a-f0-9]{64}$/i.test(version) ? "" : suffix}`);
      page.details.add(`《${title}》${suffix}`);
    }
    pages.set(view.panel_key, page);
  }
  const join = (field: "objects" | "details") => [...pages.values()].map(page => [page.title, ...page[field]].join(" · ")).join("；");
  const title = join("objects");
  return title ? { title, detail: join("details") } : undefined;
}

/** Optional display metadata; it never changes the PanelSpec's actions or permissions. */
export function workspaceNavigation(metadata: Record<string, unknown> | undefined): WorkspaceNavigation | null {
  const value = object(metadata?.workspace);
  if (typeof value.title !== "string" || !value.title.trim()) return null;
  return {
    title: value.title,
    pages: (Array.isArray(value.pages) ? value.pages : []).flatMap(item => {
      const page = object(item);
      return typeof page.panel === "string" && page.panel && typeof page.label === "string" && page.label ? [{ panel: page.panel, label: page.label }] : [];
    }),
    // A route of this Agent (`#/…`) or of the site around it (`/#/…`); nothing else.
    // `menu: false` is a link only the page opens (`pa.open`), left out of the menu.
    links: (Array.isArray(value.links) ? value.links : []).flatMap(item => {
      const link = object(item);
      const target = typeof link.panel_context === "string" && /^[A-Za-z_][A-Za-z0-9_]*$/.test(link.panel_context) ? link.panel_context : undefined;
      const href = typeof link.href === "string" ? link.href : target ? "#/panel/" : "";
      return typeof link.label === "string" && link.label && /^\/?#\/(?!\/)/.test(href) && !/[\\\r\n]/.test(href)
        && (link.panel_context === undefined || target && /^#\/panel\/[^/?#]*(?:\?|$)/.test(href))
        ? [{ label: link.label, href, ...(target ? { panel_context: target } : {}), ...(link.context ? { context: fields(link.context) } : {}),
          ...(link.menu === false ? { menu: false as const } : {}) }] : [];
    }),
    links_label: typeof value.links_label === "string" && value.links_label ? value.links_label : "相关入口",
    state_context: fields(value.state_context),
  };
}

/** The saved state is a display hint from this frame, never a source of action authority. */
export function savedNavigationContext(state: unknown, mapping: Record<string, string>, context: Record<string, string>): Record<string, string> {
  const value = object(state);
  const next = { ...context };
  for (const [key, source] of Object.entries(mapping)) {
    delete next[key];
    if (typeof value[source] === "string" && value[source]) next[key] = value[source];
  }
  return next;
}

/** A declaration can name a local panel through host context, never a URL or another Agent. */
export function resolveWorkspaceLink(link: WorkspaceLink, context: Record<string, string>): WorkspaceLink | undefined {
  if (!link.panel_context) return link;
  const key = context[link.panel_context];
  if (!key || !/^[A-Za-z0-9_][A-Za-z0-9_.-]{0,255}$/.test(key) || key.includes("..")) return undefined;
  const search = link.href.includes("?") ? link.href.slice(link.href.indexOf("?")) : "";
  return { ...link, href: `#/panel/${encodeURIComponent(key)}${search}`, panel_context: undefined };
}

export function linkHref(declared: WorkspaceLink, context: Record<string, string>, returnTo?: string) {
  const link = resolveWorkspaceLink(declared, context);
  if (!link) return undefined;
  const [path, search] = link.href.split("?");
  const query = new URLSearchParams(search);
  const values = link.context ? Object.fromEntries(Object.entries(link.context).map(([key, source]) => [key, context[source]])) : context;
  for (const [key, value] of Object.entries(values)) if (value && !query.has(key)) query.set(key, value);
  if (returnTo && ["#/connections", "#/prefs"].includes(path) && !query.has("return")) query.set("return", returnTo);
  return `${path}${query.size ? `?${query}` : ""}`;
}

/**
 * The way back to the conversation a page came from, when the owner can read that conversation. One the console
 * does not draw (the Agent's own, the operating loop's) is empty to the owner, and this page is already where the
 * Agent's work shows: nothing leads back there.
 */
export function workBack(session: string | undefined, conversation: boolean | undefined): { label: string; href: string } | undefined {
  return session && conversation === true ? { label: "返回原对话", href: chatHref(session) } : undefined;
}

/** The places a plugin points at from its work pages, as the bar lists them (`menu: false` ones are the page's own). */
export function workLinks(navigation: WorkspaceNavigation, active: string, context: Record<string, string>) {
  const items = navigation.links.filter(link => link.menu !== false)
    .flatMap(link => { const href = linkHref(link, context, panelHref(active, context)); return href ? [{ label: link.label, href }] : []; });
  return items.length ? { label: navigation.links_label, items } : undefined;
}

/** The pages a plugin's work is made of, as the bar draws them: its row of tabs, this page lit. */
export function workPageTabs(navigation: WorkspaceNavigation, panels: PanelEntry[], active: string, context: Record<string, string>) {
  const query = new URLSearchParams(context).toString();
  const items = navigation.pages.filter(page => panels.some(panel => panel.key === page.panel))
    .map(page => ({ label: page.label, href: `#/panel/${encodeURIComponent(page.panel)}${query ? `?${query}` : ""}`, active: page.panel === active }));
  return items.length > 1 ? { label: "相关界面", items } : undefined;
}

export function WorkContext({ panels, active, context, navigation, tools, conversation, inBar = false }: {
  panels: PanelEntry[]; active: string; context: Record<string, string>; navigation: WorkspaceNavigation;
  tools?: ReactNode;
  /** Whether the owner can read the conversation this page came from (`useConversationDrawn`); not answered yet: undefined. */
  conversation?: boolean;
  /** The Agent's bar above carries the page tabs, the way back, the places to go and the reload: nothing is drawn here. */
  inBar?: boolean;
}) {
  const session = context.session_id || context.session;
  const query = new URLSearchParams(context).toString();
  const pages = navigation.pages.filter(page => panels.some(panel => panel.key === page.panel));
  const hasOverviewChoice = workspaceEntries(panels.filter(panel => panel.slot === "main" && panel.audience === "owner")).length > 1;
  if (inBar) return null;
  const listed = navigation.links.filter(link => link.menu !== false);
  const preparation = listed.length ? <details className="work-context-preparation"><summary><Icon name="settings" size={15} />{navigation.links_label}</summary><div>
    {listed.map(link => { const href = linkHref(link, context, panelHref(active, context));
      return href ? <a key={href} href={href}>{link.label}</a> : null; })}
  </div></details> : null;
  // The way back to the conversation this page came from. One the console does not draw (the Agent's own, the operating
  // loop's) is empty to the owner, and this page is already where the Agent's work shows: nothing leads back there.
  // While the answer is not in, the link holds its place unseen, so nothing beside it moves when it arrives.
  const back = !session || conversation === false ? null
    : conversation === undefined ? <a className="work-context-return" aria-hidden="true" tabIndex={-1}><Icon name="chat" size={15} />返回原对话</a>
    : <a className="work-context-return" href={chatHref(session)}><Icon name="chat" size={15} />返回原对话</a>;
  return <header className="work-context">
    <div className="work-context-top">{hasOverviewChoice ? <><a href={WORKSPACE_HREF}><Icon name="back" size={15} />Agent 概览</a><span>{navigation.title}</span></> : null}
      <div className="work-context-tools" role="group" aria-label="界面操作">{back}{tools}{!pages.length ? preparation : null}</div></div>
    {pages.length ? <div className="work-context-bottom"><nav aria-label="相关界面">{pages.map(page => <a key={page.panel}
      href={`#/panel/${encodeURIComponent(page.panel)}${query ? `?${query}` : ""}`} aria-current={page.panel === active ? "page" : undefined}>{page.label}</a>)}</nav>
      {preparation}
    </div> : null}
  </header>;
}
